import { useParams, Link, Navigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Check, Users, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Breadcrumb } from "@/components/Breadcrumb";
import { SERVICES, PROCESS } from "@/data/content";
import { Seo, serviceLd, faqLd, breadcrumbLd } from "@/components/Seo";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;
  const Icon = Icons[service.icon] || Icons.Shield;
  const related = (service.related || []).map((s) => SERVICES.find((x) => x.slug === s)).filter(Boolean);

  return (
    <div>
      <Seo
        title={`${service.name} NZ | Quantum Cover`}
        description={service.what.slice(0, 155)}
        path={`/services/${service.slug}`}
        image={service.image}
        jsonLd={[
          serviceLd(service.name, service.what, `/services/${service.slug}`),
          faqLd(service.faqs),
          breadcrumbLd([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: service.name, path: `/services/${service.slug}` }]),
        ]}
      />
      {/* Hero */}
      <section className="relative bg-[#0B3D2E] text-white pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 qc-grid-bg opacity-30" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <Breadcrumb to="/services" label="All services" current={service.name} testId="service-breadcrumb" tone="dark" />
            <span className="mt-6 grid place-items-center h-14 w-14 rounded-2xl bg-white/10 text-emerald-400"><Icon size={28} /></span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl font-bold tracking-tight">{service.name}</h1>
            <p className="mt-4 text-lg text-slate-300 max-w-lg">{service.tagline}</p>
            <Link to="/booking"><Button size="lg" className="mt-8 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-7 py-6 text-base" data-testid="service-cta-btn">Talk to a licensed adviser <ArrowRight size={18} className="ml-1.5" /></Button></Link>
          </div>
          <div className="rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl">
            <img src={service.image} alt={service.name} className="w-full h-80 object-cover" />
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-20 space-y-16">
        {service.disclaimer && (
          <div className="rounded-2xl bg-emerald-50 border border-emerald-100 px-6 py-5 text-sm text-emerald-900/80 leading-relaxed" data-testid="service-disclaimer">
            {service.disclaimer}
          </div>
        )}
        {/* What & Why */}
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
            <h2 className="font-display text-2xl font-semibold text-[#0B3D2E]">What it is</h2>
            <p className="mt-3 text-slate-600 leading-relaxed">{service.what}</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
            <h2 className="font-display text-2xl font-semibold text-[#0B3D2E]">Why it matters</h2>
            <p className="mt-3 text-slate-600 leading-relaxed">{service.why}</p>
          </div>
        </div>

        {/* Who it's for */}
        <div>
          <div className="flex items-center gap-2.5 mb-5"><Users size={20} className="text-emerald-600" /><h2 className="font-display text-2xl font-semibold text-[#0B3D2E]">Who it's for</h2></div>
          <div className="grid sm:grid-cols-2 gap-3">
            {service.who.map((w) => (
              <div key={w} className="flex items-start gap-3 bg-white rounded-2xl border border-slate-200 px-5 py-4">
                <Check size={18} className="text-emerald-600 mt-0.5 shrink-0" /><span className="text-slate-700">{w}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Misconceptions */}
        <div>
          <div className="flex items-center gap-2.5 mb-5"><AlertCircle size={20} className="text-emerald-600" /><h2 className="font-display text-2xl font-semibold text-[#0B3D2E]">Common misconceptions</h2></div>
          <div className="space-y-4">
            {service.misconceptions.map((m) => (
              <div key={m.m} className="bg-white rounded-2xl border border-slate-200 p-6">
                <p className="font-display font-medium text-[#0B3D2E]">"{m.m}"</p>
                <p className="mt-2 text-slate-600 leading-relaxed"><span className="text-emerald-700 font-medium">The reality: </span>{m.t}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Process */}
        <div>
          <h2 className="font-display text-2xl font-semibold text-[#0B3D2E] mb-5">How we help</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {PROCESS.map((p) => (
              <div key={p.step} className="flex gap-4 bg-white rounded-2xl border border-slate-200 p-5">
                <span className="font-display text-2xl font-bold text-emerald-600/50">{p.step}</span>
                <div><p className="font-display font-semibold text-[#0B3D2E]">{p.title}</p><p className="text-sm text-slate-600 mt-1">{p.desc}</p></div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQs */}
        <div>
          <h2 className="font-display text-2xl font-semibold text-[#0B3D2E] mb-5">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="bg-white rounded-3xl border border-slate-200 px-6">
            {service.faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-display font-medium text-[#0B3D2E] hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* CTA */}
        {related.length > 0 && (
          <div>
            <h2 className="font-display text-2xl font-semibold text-[#0B3D2E] mb-5">Cover that works well together</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {related.map((r) => {
                const RIcon = Icons[r.icon] || Icons.Shield;
                return (
                  <Link key={r.slug} to={`/services/${r.slug}`} data-testid={`related-${r.slug}`} className="group bg-white rounded-2xl border border-slate-200 p-6 hover:border-emerald-300 hover:-translate-y-0.5 transition-[transform,border-color]">
                    <span className="grid place-items-center h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 mb-4"><RIcon size={20} /></span>
                    <h3 className="font-display font-semibold text-[#0B3D2E] leading-snug">{r.name}</h3>
                    <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 group-hover:gap-2.5 transition-all">Learn more <ArrowRight size={14} /></span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="rounded-3xl bg-[#0B3D2E] px-6 py-10 sm:p-10 text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white">Have questions about {service.name.toLowerCase()}?</h2>
          <p className="mt-3 text-slate-300">Book a free, no-obligation consultation with a licensed adviser.</p>
          <Link to="/booking"><Button size="lg" className="mt-6 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-base">Book a Free Consultation</Button></Link>
        </div>
      </section>
    </div>
  );
}
